/**
 * 分镜方案 React Query Hooks
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  storyboardPlansApi,
  type CreateStoryboardPlanDto,
  type ProjectStoryboardPlansResponse,
  type StoryboardPlanResponse,
  type UpdateStoryboardPlanDto,
} from '@/services/api/storyboard-plans';
import { useAppStore } from '@/stores/appStore';
import { invalidateStoryboardBoardContentQueries } from '@/hooks/api/useStoryboardBoard';

export const storyboardPlanKeys = {
  all: ['storyboard-plans'] as const,
  byProject: (projectId: string) => [...storyboardPlanKeys.all, 'project', projectId] as const,
  byStoryboard: (storyboardId: string, planType: number) =>
    [...storyboardPlanKeys.all, 'storyboard', storyboardId, planType] as const,
  detail: (planId: string) => [...storyboardPlanKeys.all, 'detail', planId] as const,
};

type ToggleStoryboardPlanFinalizedVariables = {
  planId: string;
  storyboardId: string;
};

type StoryboardPlanListSnapshot = {
  queryKey: readonly unknown[];
  data: StoryboardPlanResponse[] | undefined;
};

type StoryboardPlansQueryOptions = {
  enabled?: boolean;
};

export function shouldEnableStoryboardPlansQuery(
  storyboardId: string | null | undefined,
  enabled = true
): boolean {
  return Boolean(storyboardId) && enabled;
}

export function isStoryboardPlanListQueryKey(
  queryKey: readonly unknown[],
  storyboardId: string,
  planType: number
): boolean {
  return (
    queryKey[0] === 'storyboard-plans' &&
    queryKey[1] === 'storyboard' &&
    queryKey[2] === storyboardId &&
    queryKey[3] === planType
  );
}

export function applyFinalizedPlanOptimisticUpdate(
  plans: StoryboardPlanResponse[],
  planId: string
): StoryboardPlanResponse[] {
  const updated = plans.map((plan) => ({
    ...plan,
    isFinalized: plan.planId === planId,
  }));
  return [...updated].sort((a, b) => {
    if (a.isFinalized && !b.isFinalized) return -1;
    if (!a.isFinalized && b.isFinalized) return 1;
    return (
      b.orderIndex - a.orderIndex ||
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  });
}

export function getProjectStoryboardPlanGroup(
  groups: ProjectStoryboardPlansResponse[] | undefined,
  storyboardId: string
): ProjectStoryboardPlansResponse {
  return (
    groups?.find((group) => group.storyboardId === storyboardId) ?? {
      storyboardId,
      imagePlans: [],
      videoPlans: [],
    }
  );
}

export function useProjectStoryboardPlans(
  projectId: string | null | undefined,
  options: StoryboardPlansQueryOptions = {}
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: storyboardPlanKeys.byProject(projectId ?? ''),
    queryFn: async () => {
      const groups = await storyboardPlansApi.getProjectStoryboardPlans(projectId!);
      groups.forEach((group) => {
        queryClient.setQueryData(
          storyboardPlanKeys.byStoryboard(group.storyboardId, 1),
          group.imagePlans
        );
        queryClient.setQueryData(
          storyboardPlanKeys.byStoryboard(group.storyboardId, 2),
          group.videoPlans
        );
      });
      return groups;
    },
    enabled: Boolean(projectId) && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

/**
 * 获取分镜下指定类型方案（planType: 1=图片，2=视频）
 */
export function useStoryboardPlans(
  storyboardId: string | null | undefined,
  planType: number,
  options: StoryboardPlansQueryOptions = {}
) {
  return useQuery({
    queryKey: storyboardPlanKeys.byStoryboard(storyboardId ?? '', planType),
    queryFn: () => storyboardPlansApi.getPlans(storyboardId!, planType),
    enabled: shouldEnableStoryboardPlansQuery(storyboardId, options.enabled ?? true),
    staleTime: 30_000,
  });
}

/**
 * 获取分镜下所有图片方案（planType=1）
 */
export function useStoryboardImagePlans(
  storyboardId: string | null | undefined,
  options?: StoryboardPlansQueryOptions
) {
  return useStoryboardPlans(storyboardId, 1, options);
}

/**
 * 获取分镜下所有视频方案（planType=2），通常只有一个默认方案
 */
export function useStoryboardVideoPlans(
  storyboardId: string | null | undefined,
  options?: StoryboardPlansQueryOptions
) {
  return useStoryboardPlans(storyboardId, 2, options);
}

/**
 * 获取单个方案详情
 */
export function useStoryboardPlan(planId: string | null | undefined) {
  return useQuery({
    queryKey: storyboardPlanKeys.detail(planId ?? ''),
    queryFn: () => storyboardPlansApi.getPlan(planId!),
    enabled: !!planId,
    staleTime: 30_000,
  });
}

/**
 * 失效分镜看板内容缓存
 *
 * 看板 storyboard 轨道携带 mainImageUrl，主图方案变化后不失效会让预览行显示旧图。
 */
function invalidateBoardContentForCurrentProject(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  void invalidateStoryboardBoardContentQueries(
    queryClient,
    useAppStore.getState().currentProjectId
  );
}

/** 仅失效分镜下图片方案缓存（planType=1） */
export function invalidateStoryboardImagePlanQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storyboardId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: storyboardPlanKeys.byStoryboard(storyboardId, 1),
  });
  invalidateBoardContentForCurrentProject(queryClient);
}

/** 失效分镜下图片和视频两类方案缓存 */
export function invalidateStoryboardPlanQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  storyboardId: string
) {
  void queryClient.invalidateQueries({
    queryKey: storyboardPlanKeys.byStoryboard(storyboardId, 1),
  });
  void queryClient.invalidateQueries({
    queryKey: storyboardPlanKeys.byStoryboard(storyboardId, 2),
  });
  void queryClient.invalidateQueries({
    queryKey: [...storyboardPlanKeys.all, 'project'],
  });
  invalidateBoardContentForCurrentProject(queryClient);
}

/**
 * 返回一个函数，调用后失效指定分镜下的图片和视频两类方案缓存。
 */
export function useInvalidateStoryboardPlanQueries() {
  const queryClient = useQueryClient();
  return useCallback(
    (storyboardId: string) => invalidateStoryboardPlanQueries(queryClient, storyboardId),
    [queryClient]
  );
}

export function useRefetchStoryboardImagePlanQueries() {
  const queryClient = useQueryClient();
  return useCallback(
    (storyboardId: string) =>
      queryClient.refetchQueries({
        queryKey: storyboardPlanKeys.byStoryboard(storyboardId, 1),
      }),
    [queryClient]
  );
}

/**
 * 新建方案
 */
export function useCreateStoryboardPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storyboardId, data }: { storyboardId: string; data: CreateStoryboardPlanDto }) =>
      storyboardPlansApi.createPlan(storyboardId, data),
    onSuccess: (createdPlan, variables) => {
      queryClient.setQueryData<StoryboardPlanResponse[]>(
        storyboardPlanKeys.byStoryboard(variables.storyboardId, createdPlan.planType),
        (current = []) => {
          const withoutDuplicate = current.filter((plan) => plan.planId !== createdPlan.planId);
          return [...withoutDuplicate, createdPlan].sort((a, b) => {
            if (a.orderIndex !== b.orderIndex) return b.orderIndex - a.orderIndex;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
        }
      );
      queryClient.setQueryData(storyboardPlanKeys.detail(createdPlan.planId), createdPlan);
      invalidateStoryboardPlanQueries(queryClient, variables.storyboardId);
    },
  });
}

/**
 * 更新方案
 */
export function useUpdateStoryboardPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: UpdateStoryboardPlanDto }) =>
      storyboardPlansApi.updatePlan(planId, data),
    onSuccess: (plan) => {
      invalidateStoryboardPlanQueries(queryClient, plan.storyboardId);
      queryClient.setQueryData(storyboardPlanKeys.detail(plan.planId), plan);
    },
  });
}

/**
 * 删除方案
 * onSuccess 同时失效方案列表与该分镜下的图片查询，避免残留已删除方案的数据
 */
export function useDeleteStoryboardPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      planId,
      storyboardId: _storyboardId,
    }: {
      planId: string;
      storyboardId: string;
    }) => storyboardPlansApi.deletePlan(planId),
    onSuccess: (_, variables) => {
      invalidateStoryboardPlanQueries(queryClient, variables.storyboardId);
      queryClient.removeQueries({
        queryKey: storyboardPlanKeys.detail(variables.planId),
      });
      // 同步失效该分镜下的图片列表缓存
      // 注意：直接内联 key 结构避免从 useStoryboardImages 循环导入
      void queryClient.invalidateQueries({
        queryKey: ['storyboard-images', variables.storyboardId],
      });
    },
  });
}

/**
 * 切换主分镜图方案
 */
export function useToggleStoryboardPlanFinalized() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId }: ToggleStoryboardPlanFinalizedVariables) =>
      storyboardPlansApi.updatePlan(planId, { isFinalized: true }),
    onMutate: async ({ planId, storyboardId }) => {
      // 取消进行中的查询避免覆盖乐观更新
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: storyboardPlanKeys.byStoryboard(storyboardId, 1),
        }),
        queryClient.cancelQueries({
          queryKey: storyboardPlanKeys.byStoryboard(storyboardId, 2),
        }),
      ]);

      const snapshots: StoryboardPlanListSnapshot[] = [];
      [1, 2].forEach((planType) => {
        const allKeys = queryClient.getQueryCache().findAll({
          predicate: (query) => {
            const key = query.queryKey as readonly unknown[];
            return isStoryboardPlanListQueryKey(key, storyboardId, planType);
          },
        });

        allKeys.forEach((query) => {
          const key = query.queryKey as readonly unknown[];
          snapshots.push({
            queryKey: key,
            data: queryClient.getQueryData<StoryboardPlanResponse[]>(key),
          });
          queryClient.setQueryData(key, (old: StoryboardPlanResponse[] | undefined) => {
            if (!old) return old;
            return applyFinalizedPlanOptimisticUpdate(old, planId);
          });
        });
      });

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      context?.snapshots.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (plan, _err, { storyboardId }) => {
      if (plan) {
        queryClient.setQueryData(storyboardPlanKeys.detail(plan.planId), plan);
      }
      invalidateStoryboardPlanQueries(queryClient, storyboardId);
    },
  });
}

export function useSetStoryboardPlanSelectedResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, resourceId }: { planId: string; resourceId: string }) =>
      storyboardPlansApi.setSelectedResource(planId, resourceId),
    onSuccess: (plan) => {
      invalidateStoryboardPlanQueries(queryClient, plan.storyboardId);
      queryClient.setQueryData(storyboardPlanKeys.detail(plan.planId), plan);
    },
  });
}
